import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { GRAPHQL_ENDPOINT } from "src/constants/endpoints";
import {
  ACTIVATE_PROMOTION,
  ARCHIVE_PROMOTION,
  CREATE_PROMOTION,
  PAUSE_PROMOTION,
} from "src/constants/mutations";
import { GET_PROMOTIONS } from "src/constants/queries";
import { useAuth } from "src/contexts/auth-provider";
import { useAxiosInterceptor } from "src/hooks/useAxiosInterceptor";
import { BtnLoader } from "src/Loader";
import { failureToast, successToast } from "src/utils/toasts";
import { formatDate } from "src/utils/date";
import useOutsideClick from "src/hooks/useOutsideClick";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "src/features/Events/date-picker.css";

type PromotionStatus = "DRAFT" | "ACTIVE" | "PAUSED" | "EXPIRED" | "ARCHIVED";

type Promotion = {
  correlationId: string;
  name: string;
  description?: string | null;
  status: PromotionStatus;
  promotionType: "ORDER_PERCENT_OFF" | "ORDER_AMOUNT_OFF" | "FREE_DELIVERY";
  discountValue: number;
  startsAt?: number | null;
  expiresAt?: number | null;
  maxRedemptionsTotal?: number | null;
  appliesToOrderType: "ALL" | "PICK_UP" | "DELIVERY" | "DINE_IN";
};

const DEFAULT_FORM = {
  name: "",
  description: "",
  promotionType: "ORDER_PERCENT_OFF",
  discountValue: "",
  startsAt: null as Date | null,
  expiresAt: null as Date | null,
  maxRedemptionsTotal: "",
  appliesToOrderType: "ALL",
};

type DropdownOption = { value: string; label: string };

const DropdownField = ({
  value,
  options,
  onChange,
  ariaLabel,
}: {
  value: string;
  options: DropdownOption[];
  onChange: (next: string) => void;
  ariaLabel: string;
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useOutsideClick({ ref, handler: () => setOpen(false) });

  const selectedLabel =
    options.find((option) => option.value === value)?.label || options[0]?.label || "";

  return (
    <div className="relative text-left" ref={ref}>
      <button
        type="button"
        className="inline-flex h-[40px] w-full items-center justify-between gap-x-1.5 whitespace-nowrap rounded-md border border-[#E5E7EB] bg-white px-3 py-2 text-sm text-[#111827] hover:border-[#d1d5db] hover:bg-[#fafafa] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FD7657]/40 focus-visible:border-[#FD7657] transition-colors"
        aria-expanded={open}
        aria-haspopup="true"
        aria-label={ariaLabel}
        onClick={() => setOpen((prev) => !prev)}
      >
        <span>{selectedLabel}</span>
        <svg
          className="-mr-1 h-5 w-5 text-[#9ca3af]"
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden="true"
        >
          <path
            fillRule="evenodd"
            d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
            clipRule="evenodd"
          />
        </svg>
      </button>
      {open ? (
        <div
          className="absolute right-0 z-[100] mt-2 w-full origin-top-right rounded-md border border-[#E5E7EB] bg-white shadow-lg focus:outline-none"
          role="menu"
          aria-orientation="vertical"
        >
          <div className="py-1" role="none">
            {options.map((option) => (
              <button
                key={option.value}
                type="button"
                className={`block w-full px-4 py-2 text-left text-sm ${
                  value === option.value
                    ? "bg-[#fef3ef] font-medium text-[#111827]"
                    : "text-[#374151] hover:bg-[#f9fafb]"
                }`}
                role="menuitem"
                onClick={() => {
                  onChange(option.value);
                  setOpen(false);
                }}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
};

const CustomerPromotions = () => {
  const { hasRestaurant } = useAuth();
  const { axBe } = useAxiosInterceptor();

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [search, setSearch] = useState("");
  const [promotions, setPromotions] = useState<Promotion[]>([]);
  const [form, setForm] = useState(DEFAULT_FORM);
  const DateInput = forwardRef<HTMLInputElement, { value?: string; onClick?: () => void; placeholder?: string }>(
    ({ value, onClick, placeholder }, ref) => (
      <input
        type="text"
        className="h-[40px] w-full rounded-lg border border-[#F1F1F2] px-3 text-sm"
        onClick={onClick}
        ref={ref}
        value={value || ""}
        placeholder={placeholder}
        readOnly
      />
    )
  );

  const promotionTypeOptions: DropdownOption[] = [
    { value: "ORDER_PERCENT_OFF", label: "Order % off" },
    { value: "ORDER_AMOUNT_OFF", label: "Order amount off (cents)" },
    { value: "FREE_DELIVERY", label: "Free delivery" },
  ];
  const appliesToOptions: DropdownOption[] = [
    { value: "ALL", label: "All order types" },
    { value: "PICK_UP", label: "Pick up" },
    { value: "DELIVERY", label: "Delivery" },
    { value: "DINE_IN", label: "Dine in" },
  ];

  const fetchPromotions = useCallback(async () => {
    if (!hasRestaurant?.correlationId) return;
    setLoading(true);
    try {
      const { data } = await axBe.post(GRAPHQL_ENDPOINT, {
        query: GET_PROMOTIONS,
        variables: { storeCorrelationId: hasRestaurant.correlationId },
      });
      setPromotions(data?.data?.promotionsByStore || []);
    } catch (error) {
      failureToast("Failed to load promotions.");
    } finally {
      setLoading(false);
    }
  }, [axBe, hasRestaurant?.correlationId]);

  useEffect(() => {
    fetchPromotions();
  }, [fetchPromotions]);

  const filteredPromotions = useMemo(() => {
    const keyword = search.trim().toLowerCase();
    if (!keyword) return promotions;
    return promotions.filter((promotion) => {
      const name = promotion.name?.toLowerCase() || "";
      const status = promotion.status?.toLowerCase() || "";
      const type = promotion.promotionType?.toLowerCase() || "";
      return (
        name.includes(keyword) ||
        status.includes(keyword) ||
        type.includes(keyword)
      );
    });
  }, [promotions, search]);

  const createPromotion = async () => {
    if (!hasRestaurant?.correlationId) return;
    if (!form.name.trim()) {
      failureToast("Promotion name is required.");
      return;
    }
    if (!form.discountValue) {
      failureToast("Discount value is required.");
      return;
    }

    setSubmitting(true);
    try {
      await axBe.post(GRAPHQL_ENDPOINT, {
        query: CREATE_PROMOTION,
        variables: {
          input: {
            storeCorrelationId: hasRestaurant.correlationId,
            name: form.name.trim(),
            description: form.description.trim() || null,
            promotionType: form.promotionType,
            discountValue: Number(form.discountValue),
            startsAt: form.startsAt ? form.startsAt.getTime() : null,
            expiresAt: form.expiresAt ? form.expiresAt.getTime() : null,
            maxRedemptionsTotal: form.maxRedemptionsTotal
              ? Number(form.maxRedemptionsTotal)
              : null,
            appliesToOrderType: form.appliesToOrderType,
            firstTimeCustomerOnly: false,
          },
        },
      });
      successToast("Promotion created as draft.");
      setForm(DEFAULT_FORM);
      fetchPromotions();
    } catch (error) {
      failureToast("Unable to create promotion.");
    } finally {
      setSubmitting(false);
    }
  };

  const updateStatus = async (
    promotionCorrelationId: string,
    next: "ACTIVE" | "PAUSED" | "ARCHIVED"
  ) => {
    if (!hasRestaurant?.correlationId) return;
    const query =
      next === "ACTIVE"
        ? ACTIVATE_PROMOTION
        : next === "PAUSED"
          ? PAUSE_PROMOTION
          : ARCHIVE_PROMOTION;
    try {
      await axBe.post(GRAPHQL_ENDPOINT, {
        query,
        variables: {
          storeCorrelationId: hasRestaurant.correlationId,
          promotionCorrelationId,
        },
      });
      successToast(`Promotion ${next.toLowerCase()}.`);
      fetchPromotions();
    } catch (error) {
      failureToast(`Failed to ${next.toLowerCase()} promotion.`);
    }
  };

  return (
    <div className="space-y-4">
      <div className="rounded-xl bg-white p-4 sm:p-5">
        <h3 className="text-base font-semibold text-[#252525]">Create promotion</h3>
        <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
          <input
            className="h-[40px] rounded-lg border border-[#F1F1F2] px-3 text-sm"
            placeholder="Promotion name"
            value={form.name}
            onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
          />
          <DropdownField
            value={form.promotionType}
            options={promotionTypeOptions}
            onChange={(next) => setForm((prev) => ({ ...prev, promotionType: next }))}
            ariaLabel="Select promotion type"
          />
          <input
            className="h-[40px] rounded-lg border border-[#F1F1F2] px-3 text-sm"
            placeholder="Discount value"
            type="number"
            value={form.discountValue}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, discountValue: e.target.value }))
            }
          />
          <input
            className="h-[40px] rounded-lg border border-[#F1F1F2] px-3 text-sm"
            placeholder="Max redemptions total"
            type="number"
            value={form.maxRedemptionsTotal}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, maxRedemptionsTotal: e.target.value }))
            }
          />
          <input
            className="h-[40px] rounded-lg border border-[#F1F1F2] px-3 text-sm md:col-span-2"
            placeholder="Description (optional)"
            value={form.description}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, description: e.target.value }))
            }
          />
          <div className="min-w-0">
            <DatePicker
              selected={form.startsAt}
              onChange={(date) => setForm((prev) => ({ ...prev, startsAt: date }))}
              showTimeSelect
              dateFormat="MM/dd/yyyy h:mm aa"
              placeholderText="Start date and time"
              customInput={<DateInput placeholder="Start date and time" />}
              className="w-full"
            />
          </div>
          <div className="min-w-0">
            <DatePicker
              selected={form.expiresAt}
              onChange={(date) => setForm((prev) => ({ ...prev, expiresAt: date }))}
              showTimeSelect
              dateFormat="MM/dd/yyyy h:mm aa"
              placeholderText="End date and time"
              customInput={<DateInput placeholder="End date and time" />}
              className="w-full"
              minDate={form.startsAt || undefined}
            />
          </div>
          <DropdownField
            value={form.appliesToOrderType}
            options={appliesToOptions}
            onChange={(next) =>
              setForm((prev) => ({ ...prev, appliesToOrderType: next }))
            }
            ariaLabel="Select applicable order type"
          />
        </div>
        <div className="mt-3 flex justify-end">
          {submitting ? (
            <BtnLoader />
          ) : (
            <button
              className="h-[38px] rounded-lg bg-orange px-4 text-sm text-white"
              onClick={createPromotion}
            >
              Create draft
            </button>
          )}
        </div>
      </div>

      <div className="rounded-xl bg-white p-4 sm:p-5">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <h3 className="text-base font-semibold text-[#252525]">Promotions</h3>
          <input
            type="search"
            className="h-[40px] w-full rounded-lg border border-[#F1F1F2] px-3 text-sm sm:w-[280px]"
            placeholder="Search by name, status, type"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {loading ? (
          <div className="py-6 text-center">
            <BtnLoader />
          </div>
        ) : filteredPromotions.length === 0 ? (
          <p className="py-6 text-center text-sm text-[#9B9B9B]">
            No promotions found.
          </p>
        ) : (
          <div className="mt-4 overflow-x-auto">
            <table className="min-w-[760px] w-full text-left text-sm">
              <thead className="border-b border-borderGray text-xs text-[#9B9B9B]">
                <tr>
                  <th className="px-3 py-3">Name</th>
                  <th className="px-3 py-3">Status</th>
                  <th className="px-3 py-3">Type</th>
                  <th className="px-3 py-3">Value</th>
                  <th className="px-3 py-3">Expires</th>
                  <th className="px-3 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredPromotions.map((promotion) => (
                  <tr key={promotion.correlationId} className="border-b border-[#EBF5FF]">
                    <td className="px-3 py-3">
                      <p className="font-medium text-[#252525]">{promotion.name}</p>
                      {promotion.description ? (
                        <p className="text-xs text-[#7A7A7A]">{promotion.description}</p>
                      ) : null}
                    </td>
                    <td className="px-3 py-3">{promotion.status}</td>
                    <td className="px-3 py-3">{promotion.promotionType}</td>
                    <td className="px-3 py-3">{promotion.discountValue}</td>
                    <td className="px-3 py-3">
                      {promotion.expiresAt ? formatDate(promotion.expiresAt) : "-"}
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex justify-end gap-2">
                        {promotion.status !== "ACTIVE" && promotion.status !== "ARCHIVED" ? (
                          <button
                            className="rounded-lg bg-[#FD76571A] px-3 py-1.5 text-xs text-[#FD7657]"
                            onClick={() => updateStatus(promotion.correlationId, "ACTIVE")}
                          >
                            Activate
                          </button>
                        ) : null}
                        {promotion.status === "ACTIVE" ? (
                          <button
                            className="rounded-lg bg-[#F5F5F6] px-3 py-1.5 text-xs text-[#252525]"
                            onClick={() => updateStatus(promotion.correlationId, "PAUSED")}
                          >
                            Pause
                          </button>
                        ) : null}
                        {promotion.status !== "ARCHIVED" ? (
                          <button
                            className="rounded-lg bg-[#FEE2E2] px-3 py-1.5 text-xs text-[#B91C1C]"
                            onClick={() => updateStatus(promotion.correlationId, "ARCHIVED")}
                          >
                            Archive
                          </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomerPromotions;
